import type { Component, InjectionKey, Ref } from "vue";
import type { EntityDefinition, RelationDefinition } from "./structure";

/**
 * What a widget needs from the outside world.
 *
 * Widgets live under `dynamic/`, which may not speak HTTP — only a feature's own
 * `api` folder may (the `http-only-in-api` rule). That rule is right: a component
 * calling `fetch` is how one contract ends up written in three places.
 *
 * So the capabilities arrive by PROVIDE/INJECT, declared here as contracts and
 * implemented by the features that own them. A signature widget knows it can
 * upload; it does not know the endpoint, the auth header or the response shape.
 */

export interface UploadedFile {
	readonly id: string;
	readonly url: string;
	readonly mimeType: string;
	readonly sizeBytes: number;
	/** Como o arquivo se chama, para quem precisa mostrá-lo ou reenviá-lo. */
	readonly fileName: string;
}

export interface UploadPort {
	/**
	 * Stores bytes and returns the id to keep.
	 *
	 * The descriptor is required, mirroring the backend's I-S3: an upload nobody
	 * can attribute is an orphan the moment it lands.
	 */
	upload(input: {
		blob: Blob;
		filename: string;
		ownerKind: string;
		ownerId?: string | null;
		purpose: string;
	}): Promise<UploadedFile>;
	/**
	 * O arquivo já guardado: onde ele está e o que ele é.
	 *
	 * Devolve o arquivo inteiro e não só a URL porque a URL sozinha não diz o tipo —
	 * no driver local ela é `/v3/files/blob/<uuid>`, sem extensão nenhuma, e quem
	 * precisa saber se aquilo é uma imagem ou um documento (reenviar um anexo de
	 * resposta rápida, por exemplo) não teria de onde tirar. O servidor já responde o
	 * arquivo junto com a URL; era o adaptador que jogava fora.
	 *
	 * ── POR QUE `download` É UM PEDIDO, E NÃO ALGO QUE SE ACRESCENTA DEPOIS ──
	 *
	 * Porque no driver de objeto a URL é ASSINADA, e o `content-disposition` entra
	 * na assinatura (`object-blob-store.ts:100-105`: "signed, not appended — an
	 * override added after signing makes the fetch 403"). Quem quer que o arquivo
	 * BAIXE em vez de abrir tem que dizer isso ao servidor na hora de resolver; um
	 * `?download=1` colado numa URL já assinada é um 403 intermitente. Ausente ou
	 * falso, a mesma URL de sempre.
	 */
	urlFor(fileId: string, opts?: { download?: boolean }): Promise<UploadedFile>;
}

export interface LookupOption {
	readonly id: string;
	readonly label: string;
	/**
	 * The second line: what tells two rows of the same name apart.
	 *
	 * A contact's phone, a colleague's role, a channel's kind. It used to be
	 * glued onto the label with a middle dot, which made one long line that
	 * truncated the identifying half first — the name survived and the phone,
	 * which is the part that answers "which Maria?", was the piece that got cut.
	 */
	readonly hint?: string;
	/**
	 * Whether this row is a PERSON, so the picker can draw a face.
	 *
	 * The avatar's colour comes from the id, exactly as it does in the inbox, the
	 * contact list and the funnel card — the same person is the same shape
	 * everywhere, which is what makes a list of names scannable at all.
	 */
	readonly person?: boolean;
}

export interface LookupPort {
	/** Rows of an entity, for a reference picker. */
	search(input: { entity: string; query: string; limit: number }): Promise<LookupOption[]>;
	/** One row's label, so an existing value renders as a name and not a uuid. */
	label(input: { entity: string; id: string }): Promise<string | null>;
	/**
	 * Os valores JÁ TOMADOS de um campo com chave única de um campo só (o 1:1) —
	 * o formulário pergunta para o seletor não oferecer quem já foi reivindicado.
	 * Opcional: a porta da página pública não o tem, e o formulário degrada para
	 * "oferece tudo, o índice recusa no salvar" — a verdade continua no banco.
	 */
	takenValues?(input: {
		entity: string;
		field: string;
	}): Promise<Array<{ value: string; recordId: string }>>;
	/**
	 * A PESSOA QUE AINDA NÃO EXISTE (30/09): num workspace novo o seletor de
	 * contato não achava ninguém e não tinha porta para criar — quatro testadores
	 * ficaram presos no primeiro registro. Passa por `contacts/resolve`, como toda
	 * criação de contato: com telefone ou e-mail, a mesma pessoa é uma só.
	 * Opcional: a porta da página pública não cria ninguém.
	 */
	/** Authenticated record creation; public lookup ports omit this capability. */
	createRecord?(input: { entity: string; values: Record<string, unknown> }): Promise<LookupOption>;
	createContact?(input: { name?: string; phone?: string; email?: string }): Promise<LookupOption>;
}

/** Children of a record, for the repeatable-group widget (§7). */
export interface SubformPort {
	/** Open a saved child separately, preserving the parent's unsaved form. */
	open?(entity: string, id: string): void;
	relationsOf?(entity: string): Promise<RelationDefinition[]>;
	/**
	 * A PAGE of children, and how many there are in total.
	 *
	 * Keyed on the parent and the relation rather than on the child's foreign key
	 * because that is what the server can answer: asking for a page of the child
	 * entity and keeping the matching rows is a filter over whatever the first
	 * page happened to contain — a parent with 300 children showed the handful
	 * that survived, and nothing said so.
	 *
	 * `total` is the count for the whole relation, never the size of this page.
	 */
	list(input: {
		parentEntity: string;
		parentId: string;
		/** The relation's code, as declared on the parent. */
		relation: string;
		limit?: number;
		offset?: number;
	}): Promise<{ items: Array<Record<string, unknown> & { id: string }>; total: number }>;
	create(input: { entity: string; values: Record<string, unknown> }): Promise<{ id: string }>;
	update(input: { entity: string; id: string; values: Record<string, unknown> }): Promise<void>;
	remove(input: { entity: string; id: string }): Promise<void>;
	/**
	 * The CHILD's own definition, so the add row can draw a real widget per field
	 * — a reference searches, a choice offers its options — instead of a text box.
	 *
	 * On this port and not a query inside the widget, for the reason every port
	 * here exists: `dynamic/` is metadata-driven and never learns a route. `null`
	 * is a legitimate answer and degrades the widget to plain text inputs.
	 */
	entityOf(code: string): Promise<EntityDefinition | null>;
}

/**
 * O que a plataforma JÁ SABE e um campo pode nascer preenchido com (§2.6 #16).
 *
 * Uma porta e não uma consulta dentro do renderizador: `dynamic/` é dirigido por
 * metadado e não fala HTTP, e o que ele precisa saber é "quem está aqui" e "quem
 * é o contato escolhido" — nunca de que rota isso vem.
 *
 * Ausente é uma resposta legítima. Num formulário PÚBLICO ninguém provê esta
 * porta, e é o desenho certo: até a pessoa digitar, o visitante é anônimo, e um
 * estranho que escolhe um contato numa busca pública não pode receber de volta o
 * telefone dele.
 */
export interface AutofillPort {
	/** Quem está com o painel aberto. `null` quando não há sessão. */
	viewer(): Promise<{ name: string | null } | null>;
	/** Os dados de um contato já escolhido no próprio formulário. */
	contact(
		id: string,
	): Promise<{ name: string | null; phone: string | null; email: string | null } | null>;
	/** Um registro escolhido num `ref_entity` do formulário — a origem de `config.copyFrom`. */
	record?(entity: string, id: string): Promise<Record<string, unknown> | null>;
}

export const AUTOFILL_PORT: InjectionKey<AutofillPort> = Symbol("AUTOFILL_PORT");

/**
 * How to format dates, numbers and money — the TENANT's locale, never the
 * browser's.
 *
 * Injected rather than passed as a prop now that pages are route components:
 * a route cannot hand a page anything the URL does not carry, and threading it
 * through every screen would be worse than one provide at the shell.
 */
export interface DisplayContext {
	readonly locale: string;
	readonly currency: string;
	readonly timeZone: string;
	/**
	 * ISO 3166-1 alpha-2 — o país do WORKSPACE, e por que ele é um facto de
	 * apresentação.
	 *
	 * Ele decide qual bandeira e qual DDI o campo de telefone abre. Sem ele,
	 * `PhoneField` cai em `"BR"` para toda a gente: um negócio em Nova York
	 * anotava o telefone de um cliente e recebia `+55` costurado no número, um
	 * dígito de cada vez, sem nada na tela a explicar porquê.
	 *
	 * Opcional porque os pontos de montagem fora da casca já escrevem os três
	 * campos à mão e um quarto obrigatório partia-os todos de uma vez; ausente
	 * significa "não sei", e `PhoneField` continua a fazer o que fazia.
	 */
	readonly country?: string;
	/** Quem está na tela (id do utilizador) — o `$me` de um padrão. Ausente fora do painel. */
	readonly viewer?: string;
}

export const DISPLAY_CONTEXT: InjectionKey<Ref<DisplayContext>> = Symbol("DISPLAY_CONTEXT");

/**
 * As opções de uma escolha, quando elas vêm de uma CONSULTA e não de uma lista
 * escrita no campo (§3.11).
 *
 * A lista escrita à mão resolve "pago / pendente / cancelado" e nada mais. O caso
 * que ela não alcança é o que muda sozinho: os horários livres de um dia, os lotes
 * com saldo, as contas em aberto. Isso é um artefato `query` do próprio tenant, e o
 * campo só precisa dizer qual e quais duas colunas ler.
 *
 * Uma PORTA, e não uma consulta dentro do widget, pela mesma razão que `LOOKUP_PORT`:
 * `dynamic/` é dirigido por metadado e não fala HTTP.
 */
export interface OptionSource {
	/** O `code` do artefato `query` que devolve as linhas. */
	readonly artifact: string;
	/** A coluna que vira o valor guardado. */
	readonly valueField: string;
	/** A coluna que a pessoa lê. */
	readonly labelField: string;
	/** Valores para os `inputs` que o artefato declara, quando ele declara algum. */
	readonly inputs?: Readonly<Record<string, string>>;
}

export interface SourcedOption {
	readonly value: string;
	readonly label: string;
}

export interface OptionSourcePort {
	/**
	 * As opções, e uma frase quando não há nenhuma.
	 *
	 * `notice` nunca é decoração: uma caixa de seleção vazia e calada é o defeito
	 * mais comum de formulário que existe, e quem a vê não tem como saber se a
	 * consulta não respondeu, se o artefato está inativo ou se hoje realmente não
	 * há horário livre. Uma lista vazia SEM frase é um erro deste contrato.
	 */
	options(source: OptionSource): Promise<{ options: SourcedOption[]; notice: string | null }>;
}

/**
 * A porta de abrir o Ben — a doca global — a partir de uma página.
 *
 * Uma página não importa a doca (ela vive na moldura, fora do `<RouterView>`);
 * ela PEDE. O shell desesconde a orbe se for preciso, abre o drawer, troca para
 * o agente pedido e, quando houver `prefill`, escreve no compositor sem enviar —
 * é o que faz o click-to-edit do site virar instrução no BEN em vez de numa
 * segunda superfície de chat.
 *
 * `newSession` abre numa conversa NOVA em vez da última lembrada: "Pedir ao Ben"
 * na tela de site caía na conversa de ontem sobre um relatório (28/09), e a frase
 * pronta ia parar no meio de outro assunto.
 */
export type OpenBenPort = (input?: {
	agentCode?: string;
	prefill?: string;
	newSession?: boolean;
	/** Envia o `prefill` em vez de só o escrever — o clique de quem chamou já é o pedido. */
	send?: boolean;
}) => void;

export const OPEN_BEN: InjectionKey<OpenBenPort> = Symbol("OPEN_BEN");

export const UPLOAD_PORT: InjectionKey<UploadPort> = Symbol("UPLOAD_PORT");
export const LOOKUP_PORT: InjectionKey<LookupPort> = Symbol("LOOKUP_PORT");
export const SUBFORM_PORT: InjectionKey<SubformPort> = Symbol("SUBFORM_PORT");
export const OPTION_SOURCE_PORT: InjectionKey<OptionSourcePort> = Symbol("OPTION_SOURCE_PORT");

/** The shell supplies the native form to nested reference pickers without a widget registry cycle. */
export const RECORD_FORM: InjectionKey<Component> = Symbol("RECORD_FORM");
